import React, { useState, useEffect } from 'react';
import { FaTimes, FaDownload } from 'react-icons/fa';

const InstallAppBanner = ({ requestNotificationPermission }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPostInstall, setShowPostInstall] = useState(false);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later.
            setDeferredPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Show on mobile devices or small screens for testing
        const userAgent = navigator.userAgent;
        const isMobile = /android|iphone|ipad|ipod/i.test(userAgent) || window.innerWidth <= 768;
        const _isIOS = /iphone|ipad|ipod/i.test(userAgent);
        setIsIOS(_isIOS);
        
        const isNative = !!(window.Capacitor && window.Capacitor.isNative);
        const dismissed = localStorage.getItem('hideInstallAppBanner');
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

        if (isMobile && !isNative && !dismissed && !isStandalone) {
            setIsVisible(true);
        }

        const handleAppInstalled = () => {
            setIsVisible(false);
            setShowPostInstall(true);
            localStorage.setItem('hideInstallAppBanner', 'true');
            if (requestNotificationPermission) {
                requestNotificationPermission();
            }
        };

        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleDismiss = () => {
        setIsVisible(false);
        localStorage.setItem('hideInstallAppBanner', 'true');
    };

    const handleInstall = async () => {
        if (deferredPrompt) {
            // Show the install prompt
            deferredPrompt.prompt();
            // Wait for the user to respond to the prompt
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                console.log('User accepted the PWA install prompt');
                setIsVisible(false); // hide banner on successful install
                if (requestNotificationPermission) {
                    requestNotificationPermission();
                }
            }
            setDeferredPrompt(null);
        } else {
            // Fallback: Instruct user how to install manually via browser menu
            alert('To install, please tap your browser menu (⋮) and select "Install App" or "Add to Home Screen".');
        }
    };

    if (showPostInstall) {
        return (
            <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex justify-center items-center z-[9999] p-4 animate-fade-in">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm flex flex-col items-center p-8 text-center animate-bounce-slight">
                    <div className="w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center text-4xl mb-6 shadow-inner">
                        <i className="fas fa-rocket"></i>
                    </div>
                    <h3 className="text-2xl font-black text-slate-800 mb-3">App Installed!</h3>
                    <p className="text-slate-600 font-medium mb-8 leading-relaxed">
                        QwickMed has been successfully added to your home screen. You can now close this browser and launch the app directly for the best experience!
                    </p>
                    <button 
                        onClick={() => setShowPostInstall(false)} 
                        className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-3.5 px-4 rounded-xl transition-all shadow-lg shadow-slate-800/20"
                    >
                        Got it, thanks!
                    </button>
                </div>
            </div>
        );
    }

    if (!isVisible) return null;

    return (
        <div style={styles.banner}>
            <div style={styles.content}>
                <img 
                    src="/logo192.png" 
                    alt="App Logo" 
                    style={styles.logo} 
                />
                <div style={styles.textContainer}>
                    <h4 style={styles.title}>QwickMed App</h4>
                    <p style={styles.subtitle}>Install for a better experience</p>
                </div>
            </div>
            <div style={styles.actions}>
                {isIOS ? (
                    <div style={styles.iosInstruction}>
                        Tap share icon &<br/><b>Add to Home Screen</b>
                    </div>
                ) : (
                    <button onClick={handleInstall} style={styles.installButton}>
                        <FaDownload style={{ marginRight: '5px' }}/> Install
                    </button>
                )}
                <button onClick={handleDismiss} style={styles.closeButton}>
                    <FaTimes />
                </button>
            </div>
        </div>
    );
};

const styles = {
    banner: {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        backgroundColor: '#ffffff',
        padding: '12px 16px',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 9999,
        borderTop: '1px solid #e0e0e0'
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        flex: 1
    },
    logo: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        marginRight: '12px'
    },
    textContainer: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        margin: 0,
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#333'
    },
    subtitle: {
        margin: 0,
        fontSize: '12px',
        color: '#666'
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    installButton: {
        backgroundColor: '#2563eb',
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '20px',
        fontWeight: 'bold',
        fontSize: '14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap'
    },
    iosInstruction: {
        fontSize: '11px',
        color: '#666',
        textAlign: 'right',
        lineHeight: '1.2'
    },
    closeButton: {
        background: 'none',
        border: 'none',
        color: '#999',
        fontSize: '20px',
        cursor: 'pointer',
        padding: '4px'
    }
};

export default InstallAppBanner;
